import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import ReactDatePicker from "react-datepicker";
import { Button } from "../../common/Button/Button";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import downloadIcon from "../../assets/downloadsvg2.svg";
import { useHistory } from "react-router-dom";
import Spinner from "../../common/Spinner/Spinner";

// Memoized KPI Card component to avoid recreating DOM subtrees on parent renders
const DashboardKpiCard = React.memo(({ title, value, subtitle, accent, loading = false }) => {
    return (
        <div className={`dashboard-kpi-card ${accent}`}>
            <div className="dashboard-kpi-label">{title}</div>
            <div className="dashboard-kpi-value">
                {loading ? "..." : (value ?? 0)}
            </div>
            <div className="dashboard-kpi-subtitle">{subtitle}</div>
        </div>
    );
});
DashboardKpiCard.displayName = "DashboardKpiCard";

const adjustDateForStartOfDay = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    d.setTime(d.getTime() + (5.5 * 60 * 60 * 1000));
    return d;
};

const adjustDateForEndOfDay = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    d.setTime(d.getTime() + (29.5 * 60 * 60 * 1000 - 1000));
    return d;
};

const getCurrentDateFormatted = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = months[today.getMonth()];
    const year = today.getFullYear();
    return `${day}_${month}_${year}`;
};

const flattenInterventionsData = (data) => {
    if (!Array.isArray(data)) return [];
    const flattened = [];
    data.forEach((item, userIdx) => {
        const interventions = item?.interventions_availed;
        const isEvenUser = userIdx % 2 === 0;
        if (Array.isArray(interventions) && interventions.length > 0) {
            const count = interventions.length;
            interventions.forEach((intervention, index) => {
                let resourceName = "—";
                let statusVal = "—";
                if (typeof intervention === "object" && intervention !== null) {
                    resourceName = intervention?.Intervention || intervention?.sResourceID || intervention?.sResourceName || intervention?.name || "—";
                    statusVal = intervention?.Status || intervention?.status || "—";
                } else if (typeof intervention === "string" && intervention) {
                    resourceName = intervention;
                    statusVal = item?.Status || item?.status || "—";
                }

                flattened.push({
                    ...item,
                    interventions_availed: resourceName,
                    Status: statusVal,
                    status: statusVal,
                    rowSpanCount: index === 0 ? count : 1,
                    isSubRow: index !== 0,
                    userIdx: userIdx,
                    isEvenUser: isEvenUser,
                    userOrder: userIdx * 100 + index,
                });
            });
        } else {
            flattened.push({
                ...item,
                interventions_availed: (typeof interventions === "string" && interventions) ? interventions : "—",
                Status: item?.Status || item?.status || "—",
                status: item?.Status || item?.status || "—",
                rowSpanCount: 1,
                isSubRow: false,
                userIdx: userIdx,
                isEvenUser: isEvenUser,
                userOrder: userIdx * 100,
            });
        }
    });
    return flattened;
};

const userRowSpan = (params) => {
    if (params.data && params.data.rowSpanCount > 1) {
        return params.data.rowSpanCount;
    }
    return 1;
};

const getUserBgColor = (params) => {
    return params.data?.isEvenUser === false ? "#ECECEC" : "#DEDEDE";
};

const spanCellStyle = (params) => {
    return {
        backgroundColor: getUserBgColor(params),
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid black",
    };
};

const interventionCellStyle = (params) => {
    return {
        backgroundColor: getUserBgColor(params),
        display: "flex",
        alignItems: "center",
    };
};

const formatInterventionTitle = (rawTitle) => {
    if (!rawTitle) return "—";
    return String(rawTitle).trim();
};

const renderInterventionCell = (value) => {
    if (!value || (Array.isArray(value) && !value.length) || value === "—") {
        return "—";
    }

    let stringVal = "";
    if (Array.isArray(value)) {
        stringVal = value.join(", ");
    } else if (typeof value === "object" && value !== null) {
        stringVal = value?.Intervention || value?.sResourceID || value?.sResourceName || value?.name || "";
    } else {
        stringVal = String(value);
    }

    if (!stringVal || stringVal.trim() === "" || stringVal === "—") {
        return "—";
    }

    // If pipe-separated (e.g. Title|Location|Date)
    if (stringVal.includes("|")) {
        const parts = stringVal.split("|").map((p) => p.trim()).filter(Boolean);
        const title = formatInterventionTitle(parts[0]);
        const details = parts.slice(1).map((p) => p.replace(/_/g, " ").trim()).filter(Boolean).join(" | ");

        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    lineHeight: "1.35",
                    padding: "4px 0",
                    width: "100%",
                }}
                title={stringVal}
            >
                <span
                    style={{
                        color: "#1b2733",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                    }}
                >
                    {title}
                </span>
                {details ? (
                    <span
                        style={{
                        color: "#1b2733",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                    }}
                    >
                        {details}
                    </span>
                ) : null}
            </div>
        );
    }

    const formattedTitle = formatInterventionTitle(stringVal);
    return (
        <span
            title={stringVal}
            style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                // fontWeight: 500,
                color: "#1b2733",
            }}
        >
            {formattedTitle}
        </span>
    );
};

const InterventionsAvailedReports = () => {
    const partnerGridRef = useRef(null);
    const interventionsGridRef = useRef(null);
    const hasShownMobileToastRef = useRef(false);

    // Filter states
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [quickFilterText, setQuickFilterText] = useState("");
    const [partnerSearchText, setPartnerSearchText] = useState("");
    const [mobileSearchText, setMobileSearchText] = useState("");

    // Loading states
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const [kpiLoading, setKpiLoading] = useState(false);
    const [pieChartLoading, setPieChartLoading] = useState(false);
    const [partnerWiseLoading, setPartnerWiseLoading] = useState(false);
    const [eligibleUsersLoading, setEligibleUsersLoading] = useState(false);
    const [isExportLoading, setIsExportLoading] = useState(false);

    // KPI data
    const [mobilizationCountData, setMobilizationCountData] = useState(0);
    const [countData, setCountData] = useState(0);
    const [availedCount, setAvailedCount] = useState(0);
    const [activePartnerCount, setActivePartnerCount] = useState(0);

    // Pie chart data
    const [summaryData, setSummaryData] = useState([]);
    const [totalResourcesDelivered, setTotalResourcesDelivered] = useState(0);

    // Partner-wise table data
    const [partnerWiseReport, setPartnerWiseReport] = useState([]);
    const [partnerWiseTotal, setPartnerWiseTotal] = useState({ registered: 0, availed: 0, conversion: 0 });

    // Interventions availed table data
    const [eligibleUsersData, setEligibleUsersData] = useState([]);

    const history = useHistory();


    // Column definitions for Interventions Availed grid
    const eligibleUsersColumnDefs = useMemo(() => [
        {
            field: "sMobile",
            headerName: "Mobile Number",
            flex: 1,
            minWidth: 160,
            sortable: false,
            filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            cellRenderer: (params) => {
                if (params.data?.isSubRow) return "";
                return params.value || "—";
            },
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 160,
            sortable: false,
            filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            valueGetter: (params) => {
                if (params.data?.isSubRow) return "";
                const first = params.data?.FirstName || "";
                const last = params.data?.LastName || "";
                const fullName = `${first} ${last}`.trim();
                return fullName || "—";
            },
        },
        {
            field: "partner",
            headerName: "Partner",
            flex: 1,
            minWidth: 160,
            sortable: false,
            // filter: true,
            rowSpan: userRowSpan,
            cellStyle: spanCellStyle,
            cellRenderer: (params) => {
                if (params.data?.isSubRow) return "";
                return params.value || "Not Provided";
            },
        },
        {
            field: "interventions_availed",
            headerName: "Interventions Availed",
            flex: 2,
            minWidth: 200,
            sortable: false,
            // filter: true,
            cellStyle: interventionCellStyle,
            cellRenderer: (params) => renderInterventionCell(params.value),
        },
        {
            field: "Status",
            headerName: "Status",
            flex: 1,
            minWidth: 140,
            sortable: false,
            // filter: true,
            cellStyle: interventionCellStyle,
            cellRenderer: (params) => {
                const value = params.value || params.data?.status;
                if (!value || value === "—") {
                    return "—";
                }
                return value;
            },
        },
    ], []);
    const gridOptions = useMemo(() => ({
        suppressScrollOnNewData: true,
    }), []);


    const interventionsRowClassRules = useMemo(() => ({
        "interventions-user-even": (params) => params.data?.isEvenUser === true,
        "interventions-user-odd": (params) => params.data?.isEvenUser === false,
    }), []);
    // Show loading overlay for Partner-wise table
    useEffect(() => {
        if (partnerGridRef.current && partnerGridRef.current.api) {
            if (partnerWiseLoading) {
                partnerGridRef.current.api.showLoadingOverlay();
            } else if (!partnerWiseReport || partnerWiseReport.length === 0) {
                partnerGridRef.current.api.showNoRowsOverlay();
            } else {
                partnerGridRef.current.api.hideOverlay();
            }
        }
    }, [partnerWiseLoading, partnerWiseReport]);

    // Show loading overlay for Interventions Availed table
    useEffect(() => {
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            if (eligibleUsersLoading) {
                interventionsGridRef.current.api.hideOverlay();
            } else if (!eligibleUsersData || eligibleUsersData.length === 0) {
                interventionsGridRef.current.api.showNoRowsOverlay();
            } else {
                interventionsGridRef.current.api.hideOverlay();
            }
        }
    }, [eligibleUsersLoading, eligibleUsersData]);

    // Mobile resolution check (toast once)
    useEffect(() => {
        const mobileWidthBreakpoint = 768;

        const checkMobileResolution = () => {
            if (window.innerWidth < mobileWidthBreakpoint && !hasShownMobileToastRef.current) {
                hasShownMobileToastRef.current = true;
                toast("This dashboard is best viewable in a desktop browser.", {
                    autoClose: 5000,
                    type: toast.TYPE.ERROR,
                });
            }
        };

        checkMobileResolution();
        window.addEventListener("resize", checkMobileResolution);
        return () => window.removeEventListener("resize", checkMobileResolution);
    }, []);


    // Fetch interventions availed report data
    const fetchInterventionsData = useCallback((currentFromDate, currentToDate, mobileNumber) => {
        setEligibleUsersLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (currentFromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(currentFromDate);
        }
        if (currentToDate) {
            filterParameters.toDate = adjustDateForEndOfDay(currentToDate);
        }
        if (mobileNumber && mobileNumber.trim()) {
            filterParameters.sMobile = mobileNumber.trim();
        }

        return api.partnerInverntionsAvailed(filterParameters)
            .then(({ data }) => {
                setEligibleUsersData(flattenInterventionsData(data?.data || []));
            })
            .catch((err) => {
                console.error("Error fetching interventions availed data:", err);
                setEligibleUsersData([]);
            })
            .finally(() => {
                setEligibleUsersLoading(false);
            });
    }, []);

    // Optimized Dashboard Data Fetching (only 5 essential endpoints called in parallel)
    const fetchDashboardData = useCallback(async (currentFromDate, currentToDate, partnerName, mobileNumber) => {
        setIsDashboardLoading(true);
        setKpiLoading(true);
        setPieChartLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (currentFromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(currentFromDate);
        }
        if (currentToDate) {
            filterParameters.toDate = adjustDateForEndOfDay(currentToDate);
        }

        // 1. Mobilization count (Registered users)
        const mobilizationPromise = api.mobilizationCountAnekApi({ api_key: "r54B?rHXUE7v%$9t" })
            .then(({ data }) => {
                setMobilizationCountData(data?.data?.total_count ?? 0);
            })
            .catch((err) => {
                console.error("Error fetching mobilization count:", err);
                setMobilizationCountData(0);
            });

        // 2. Count metrics (Eligible, Attended, Active Partners)
        const countsPromise = api.jpmcCountData(filterParameters)
            .then(({ data }) => {
                setCountData(data?.count ?? 0);
                setAvailedCount(data?.users_attended ?? 0);
                setActivePartnerCount(data?.active_partners ?? 0);
            })
            .catch((err) => {
                console.error("Error fetching count data:", err);
                setCountData(0);
                setAvailedCount(0);
                setActivePartnerCount(0);
            });

        // 3. Interventions availed by user
        const interventionsPromise = fetchInterventionsData(currentFromDate, currentToDate, mobileNumber);

        // Resolve KPI metrics first
        await Promise.allSettled([mobilizationPromise, countsPromise]);
        setKpiLoading(false);

        // Await all other promises
        await Promise.allSettled([interventionsPromise]);
        setIsDashboardLoading(false);
    }, [fetchInterventionsData]);

    // Initial load on mount
    useEffect(() => {
        fetchDashboardData(null, null, "", "");
    }, [fetchDashboardData]);

    const handleSearch = () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            toast.error("From Date cannot be after To Date.");
            return;
        }
        fetchDashboardData(fromDate, toDate, partnerSearchText, mobileSearchText);
    };

    const handleClear = () => {
        setFromDate(null);
        setToDate(null);
        setQuickFilterText("");
        setPartnerSearchText("");
        setMobileSearchText("");
        if (interventionsGridRef.current && interventionsGridRef.current.api) {
            interventionsGridRef.current.api.setQuickFilter("");
        }
        fetchDashboardData(null, null, "", "");
    };


    const handleMobileSearch = () => {
        fetchInterventionsData(fromDate, toDate, mobileSearchText);
    };

    const handleMobileClear = () => {
        setMobileSearchText("");
        fetchInterventionsData(fromDate, toDate, "");
    };

    const handleMobileSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleMobileSearch();
        }
    };

    // Export report handler using partnerInverntionsAvailedReport API
    const handleExportReport = useCallback(async () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            toast.error("From Date cannot be after To Date.");
            return;
        }

        setIsExportLoading(true);

        const filterParameters = {
            sPrograme_type: "Anek_Programe",
        };

        if (fromDate) {
            filterParameters.fromDate = adjustDateForStartOfDay(fromDate);
        }
        if (toDate) {
            filterParameters.toDate = adjustDateForEndOfDay(toDate);
        }
        if (mobileSearchText && mobileSearchText.trim()) {
            filterParameters.sMobile = mobileSearchText.trim();
        }

        try {
            const { data } = await api.partnerInverntionsAvailedReport(filterParameters);
            if (data?.details) {
                toast(data["details"], {
                    autoClose: 5000,
                    type: toast.TYPE.SUCCESS,
                });
            } else {
                toast("Report exported successfully.", {
                    autoClose: 5000,
                    type: toast.TYPE.SUCCESS,
                });
            }

            let downloadUrl =
                data?.generated_file ||
                data?.file_url ||
                data?.url ||
                data?.data?.generated_file ||
                data?.data?.file_url;

            if (downloadUrl) {
                if (!downloadUrl.startsWith("http") && !downloadUrl.startsWith("//")) {
                    const baseUrl = process.env.REACT_APP_DOWNLOAD_API_URL || "";
                    downloadUrl = `${baseUrl}${downloadUrl.startsWith("/") ? "" : "/"}${downloadUrl}`;
                }
                window.open(encodeURI(downloadUrl), "_self");
            } else if (data instanceof Blob) {
                const url = window.URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Anek_Interventions_Availed_Report_${getCurrentDateFormatted()}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Error exporting interventions availed report:", error);
            const errorMsg = error?.response?.data?.message || error?.response?.data?.detail || "Failed to export report.";
            toast(errorMsg, {
                autoClose: 5000,
                type: toast.TYPE.ERROR,
            });
        } finally {
            setIsExportLoading(false);
        }
    }, [fromDate, toDate, mobileSearchText]);

    return (
        <div>
            {/* Filter Bar */}
            <div className="row mt-2 ml-1 mr-1 justify-content-between align-items-center">
                <div className="col-12 col-lg-8 col-xl-9">
                    <div className="row align-items-center">

                        {/* From Date */}
                        <div className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3 spled-container conv-zindex mb-2 mb-md-0">
                            <div className="width-100">
                                <ReactDatePicker
                                    dateFormat="dd/MM/yyyy"
                                    selected={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    placeholderText="From Date"
                                    minDate={new Date("01-01-2024")}
                                    maxDate={new Date()}
                                    isClearable
                                />
                            </div>
                        </div>

                        {/* To Date */}
                        <div className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3 spled-container conv-zindex mb-2 mb-md-0">
                            <div className="width-100">
                                <ReactDatePicker
                                    dateFormat="dd/MM/yyyy"
                                    selected={toDate}
                                    onChange={(date) => setToDate(date)}
                                    placeholderText="To Date"
                                    minDate={fromDate || new Date("01-01-2024")}
                                    maxDate={new Date()}
                                    isClearable
                                />
                            </div>
                        </div>

                        {/* Search and Clear Buttons */}
                        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-5 spled-container">
                            <div className="row align-items-center conv-pl10" style={{ gap: "10px" }}>
                                <div
                                    className="col-auto"
                                    style={{ padding: "0px", marginTop: "25px" }}
                                >
                                    <Button
                                        className="esp-inline-button"
                                        style={{ height: "41px", minWidth: "90px" }}
                                        onClick={handleSearch}
                                        disabled={isDashboardLoading}
                                    >
                                        {isDashboardLoading ? "Searching..." : "Search"}
                                    </Button>
                                </div>

                                <div
                                    className="col-auto"
                                    style={{ padding: "0px", marginTop: "25px" }}
                                >
                                    <Button
                                        className="esp-inline-button"
                                        style={{ height: "41px", minWidth: "90px" }}
                                        onClick={handleClear}
                                        disabled={isDashboardLoading}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Interventions Availed Reports Link */}
                <div className="col-12 col-lg-4 col-xl-3 text-left text-lg-right mt-2 mt-lg-0 pr-3">
                    <div className="d-flex align-items-center justify-content-start justify-content-lg-end" style={{ marginTop: "25px", marginBottom: "24px" }}>
                        <a
                             onClick={()=>{history.push("/home/anekdashboard/")}}
                            className="con-cursor-pointer"
                            style={{
                                color: "#78417C",
                                textDecoration: "none",
                            }}
                        >
                            <span className="text-hover" style={{ fontSize: "17px", cursor: "pointer" }}>
                                Go Back to Anek Dashboard
                            </span>
                        </a>
                    </div>
                </div>
            </div>

            {/* KPI Cards Row */}
            <div className="dashboard-kpi-row">
                {/* <DashboardKpiCard
                    title="Users registered"
                    value={mobilizationCountData}
                    subtitle="Completed registration on any landing page"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                /> */}

                <DashboardKpiCard
                    title="First Time Users"
                    value={countData}
                    subtitle="First Time users"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />

                <DashboardKpiCard
                    title="Engaged Users"
                    value={availedCount}
                    subtitle="Users who availed services"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />

                <DashboardKpiCard
                    title="Active Partners"
                    value={activePartnerCount}
                    subtitle="Tagged automatically via landing page source"
                    accent="#e3e7eb"
                    loading={kpiLoading}
                />
            </div>

            <div style={{ borderBottom: "2px solid lightgray" }}></div>



            {/* BOTTOM - Interventions Availed by Mobile Number */}
            <div className="row mt-3 mb-2">
                <div className="col-12">
                    <div
                        className="partner-data-header"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "10px",
                            marginBottom: "8px",
                        }}
                    >
                        <div className="partner-data-title">
                            <h3 className="spled-colored-label">
                                Interventions availed — by Mobile Number
                            </h3>
                        </div>

                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                            <input
                                type="text"
                                placeholder="Search by mobile number..."
                                value={mobileSearchText}
                                onChange={(e) => setMobileSearchText(e.target.value)}
                                onKeyDown={handleMobileSearchKeyDown}
                                className="form-control"
                                style={{
                                    width: "200px",
                                    height: "34px",
                                    fontSize: "13px",
                                    padding: "4px 8px",
                                }}
                            />
                            <Button
                                className="esp-inline-button"
                                style={{ height: "34px", minWidth: "75px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                onClick={handleMobileSearch}
                                disabled={eligibleUsersLoading}
                            >
                                {eligibleUsersLoading ? "Searching..." : "Search"}
                            </Button>
                            {mobileSearchText && (
                                <Button
                                    className="esp-inline-button"
                                    style={{ height: "34px", minWidth: "60px", margin: 0, padding: "0 10px", fontSize: "13px" }}
                                    onClick={handleMobileClear}
                                    disabled={eligibleUsersLoading}
                                >
                                    Clear
                                </Button>
                            )}
                            <Button
                                className="esp-inline-button"
                                style={{
                                    height: "34px",
                                    minWidth: "150px",
                                    margin: 0,
                                    padding: "0 10px",
                                    fontSize: "13px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                onClick={handleExportReport}
                                disabled={isExportLoading}
                            >
                                {isExportLoading ? "Exporting..." : "Export Report"}
                                <img
                                    src={downloadIcon}
                                    title="Export Report"
                                    alt=""
                                    style={{
                                        cursor: "pointer",
                                        width: "14px",
                                        marginLeft: "6px",
                                    }}
                                />
                            </Button>
                        </div>
                    </div>

                    <div style={{ height: "50vh", width: "100%", position: "relative" }}>
                        {(eligibleUsersLoading || isDashboardLoading) && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 10,
                                }}
                            >
                                <Spinner />
                            </div>
                        )}
                        <div
                            style={{ height: "100%", width: "100%" }}
                            className={`ag-theme-material ${(eligibleUsersLoading || isDashboardLoading) ? "set-opacity" : ""}`}
                        >
                            <AgGridReact
                                ref={interventionsGridRef}
                                rowData={eligibleUsersData}
                                columnDefs={eligibleUsersColumnDefs}
                                rowHeight={56}
                                pagination={true}
                                paginationPageSize={10}
                                suppressScrollOnNewData={true}
                                suppressRowTransform={true}
                                rowClassRules={interventionsRowClassRules}
                                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Loading...</span>'}
                                overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No records found</span>'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InterventionsAvailedReports;
